
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const formSchema = z.object({
  filename: z.string().min(1, "Filename is required"),
});

type SaveDocumentDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (filename: string) => void;
  defaultFilename?: string;
  isComplete?: boolean;
  remainingFields?: number;
};

export function SaveDocumentDialog({ 
  isOpen, 
  onClose, 
  onSave, 
  defaultFilename = "",
  isComplete = true,
  remainingFields = 0
}: SaveDocumentDialogProps) {
  // Remove any file extension from the default filename
  const cleanDefaultFilename = defaultFilename.replace(/\.\w+$/, '');
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      filename: cleanDefaultFilename,
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onSave(values.filename);
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Document</DialogTitle>
        </DialogHeader>
        
        {!isComplete && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Incomplete Document</AlertTitle>
            <AlertDescription>
              {remainingFields} field(s) still need to be filled in. You can still save the document, but it may not be complete.
            </AlertDescription>
          </Alert>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="filename"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filename</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter filename" />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
